import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { restaurants as mockRestaurants } from "@/mocks/restaurants";

const RESTAURANTS_PER_PAGE = 9;

// ── Favorites helpers ─────────────────────────────────────────
const FAVORITES_KEY = "favoriteRestaurants";

const getFavorites = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_KEY) || "[]");
  } catch {
    return [];
  }
};

const toggleFavorite = (id: string): boolean => {
  const favs = getFavorites();
  const idx = favs.indexOf(id);
  if (idx === -1) {
    favs.push(id);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
    return true;
  } else {
    favs.splice(idx, 1);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
    return false;
  }
};

// Check if a restaurant was added within the last 30 days
const isNewlyAdded = (restaurant: { approvedAt?: string; ownerSubmitted?: boolean }): boolean => {
  if (!restaurant.approvedAt) return false;
  const approvedDate = new Date(restaurant.approvedAt);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return approvedDate >= thirtyDaysAgo;
};

// Working hours per restaurant (open/close in 24h format)
// Format: [openHour, closeHour] — closeHour > 24 means past midnight
const restaurantHours: Record<string, [number, number]> = {
  "ciya-sofrasi":        [11, 22],
  "mikla":               [18, 24],
  "imam-cagdas":         [7,  22],
  "borsam-tasievi":      [10, 23],
  "hatay-sultan-sofrasi":[9,  22],
  "karadeniz-pidecisi":  [6,  20],
  "rize-serender":       [8,  21],
  "trabzon-cemilusta":   [6,  18],
  "assk-kahve":          [9,  23],
  "izmir-deniz":         [12, 24],
  "antalya-seraser":     [18, 24],
  "alanya-iskele":       [11, 23],
  "safranbolu-havuzlu":  [8,  22],
  "bolu-abant":          [9,  21],
  "konya-sifa":          [10, 22],
  "ankara-kizilirmak":   [11, 22],
};

const isOpenNow = (restaurantId: string): boolean => {
  const hours = restaurantHours[restaurantId];
  if (!hours) return true; // assume open if no data
  const now = new Date();
  const currentHour = now.getHours() + now.getMinutes() / 60;
  const [open, close] = hours;
  if (close > 24) {
    return currentHour >= open || currentHour < (close - 24);
  }
  return currentHour >= open && currentHour < close;
};

const getOpenStatus = (restaurantId: string): { open: boolean; label: string } => {
  const hours = restaurantHours[restaurantId];
  if (!hours) return { open: true, label: "Açık" };
  const open = isOpenNow(restaurantId);
  const [openH, closeH] = hours;
  const formatH = (h: number) => {
    const actual = h > 24 ? h - 24 : h;
    return `${String(actual).padStart(2, "0")}:00`;
  };
  if (open) {
    return { open: true, label: `Açık · ${formatH(closeH)}'e kadar` };
  }
  return { open: false, label: `Kapalı · ${formatH(openH)}'de açılır` };
};

const cuisines = ["Tümü", "Anadolu Mutfağı", "Modern Türk", "Gaziantep Mutfağı", "Mardin Mutfağı", "Karadeniz Mutfağı", "Ege Mutfağı"];
const priceFilters = ["Tümü", "₺", "₺₺", "₺₺₺", "₺₺₺₺"];
const regions = ["Tümü", "Marmara", "Ege", "Akdeniz", "İç Anadolu", "Güneydoğu Anadolu", "Batı Karadeniz", "Doğu Karadeniz"];

interface RegionInfo {
  name: string;
  icon: string;
  cities: string[];
  color: string;
  activeBg: string;
  description: string;
  image: string;
}

const regionInfoMap: Record<string, RegionInfo> = {
  "Marmara": {
    name: "Marmara",
    icon: "ri-building-2-line",
    cities: ["İstanbul", "Bursa", "Edirne", "Tekirdağ"],
    color: "text-violet-700",
    activeBg: "bg-violet-500",
    description: "İstanbul'un eşsiz mutfağı ve Bursa'nın tarihi lezzetleri",
    image: "https://readdy.ai/api/search-image?query=Istanbul%20Bosphorus%20Turkey%20Marmara%20region%20food%20restaurant%20city%20view&width=400&height=200&seq=reg-mar&orientation=landscape",
  },
  "Ege": {
    name: "Ege",
    icon: "ri-sailboat-line",
    cities: ["İzmir", "Muğla", "Aydın", "Çanakkale"],
    color: "text-cyan-700",
    activeBg: "bg-cyan-500",
    description: "Zeytinyağlı mezeler, taze deniz ürünleri ve Ege mutfağı",
    image: "https://readdy.ai/api/search-image?query=Izmir%20Aegean%20Turkey%20Ege%20region%20seafood%20meze%20olive%20oil%20restaurant&width=400&height=200&seq=reg-ege&orientation=landscape",
  },
  "Akdeniz": {
    name: "Akdeniz",
    icon: "ri-sun-line",
    cities: ["Antalya", "Mersin", "Adana", "Hatay"],
    color: "text-rose-700",
    activeBg: "bg-rose-500",
    description: "Adana kebabı, Hatay mutfağı ve Akdeniz lezzetleri",
    image: "https://readdy.ai/api/search-image?query=Antalya%20Mediterranean%20Turkey%20Akdeniz%20region%20kebab%20food%20restaurant&width=400&height=200&seq=reg-akd&orientation=landscape",
  },
  "İç Anadolu": {
    name: "İç Anadolu",
    icon: "ri-landscape-line",
    cities: ["Ankara", "Konya", "Kayseri", "Nevşehir"],
    color: "text-amber-700",
    activeBg: "bg-amber-500",
    description: "Kayseri mantısı, Konya etli ekmeği ve bozkır lezzetleri",
    image: "https://readdy.ai/api/search-image?query=Ankara%20Konya%20Central%20Anatolia%20Turkey%20food%20restaurant%20traditional%20cuisine&width=400&height=200&seq=reg-ica&orientation=landscape",
  },
  "Güneydoğu Anadolu": {
    name: "Güneydoğu",
    icon: "ri-ancient-gate-line",
    cities: ["Gaziantep", "Mardin", "Şanlıurfa", "Diyarbakır"],
    color: "text-red-700",
    activeBg: "bg-red-500",
    description: "Gaziantep baklavası, Mardin mutfağı ve UNESCO lezzetleri",
    image: "https://readdy.ai/api/search-image?query=Gaziantep%20Mardin%20Southeast%20Turkey%20food%20baklava%20kebab%20restaurant&width=400&height=200&seq=reg-gda&orientation=landscape",
  },
  "Batı Karadeniz": {
    name: "Batı Karadeniz",
    icon: "ri-plant-line",
    cities: ["Trabzon", "Rize", "Artvin", "Giresun"],
    color: "text-emerald-700",
    activeBg: "bg-emerald-500",
    description: "Hamsi, muhlama, karalahana ve Karadeniz'in özgün lezzetleri",
    image: "https://readdy.ai/api/search-image?query=Trabzon%20Black%20Sea%20Turkey%20Karadeniz%20region%20hamsi%20fish%20food%20restaurant&width=400&height=200&seq=reg-krd&orientation=landscape",
  },
  "Doğu Karadeniz": {
    name: "Doğu Karadeniz",
    icon: "ri-landscape-line",
    cities: ["Samsun", "Ordu", "Sinop", "Kastamonu"],
    color: "text-teal-700",
    activeBg: "bg-teal-500",
    description: "Sinop'un balık restoranları ve Ordu'nun fındık lezzetleri",
    image: "https://readdy.ai/api/search-image?query=Samsun%20Sinop%20Black%20Sea%20Turkey%20food%20fish%20restaurant%20coastal&width=400&height=200&seq=reg-dkrd&orientation=landscape",
  },
};

// IDs that have menu data in RestaurantDetail
const restaurantsWithMenu = new Set([
  "ciya-sofrasi", "mikla", "imam-cagdas", "borsam-tasievi", "hatay-sultan-sofrasi",
  "karadeniz-pidecisi", "rize-serender", "trabzon-cemilusta", "assk-kahve",
  "izmir-deniz", "antalya-seraser", "alanya-iskele", "safranbolu-havuzlu",
  "bolu-abant", "konya-sifa", "ankara-kizilirmak",
]);

const priceColors: Record<string, string> = {
  "₺": "text-emerald-600",
  "₺₺": "text-amber-600",
  "₺₺₺": "text-orange-600",
  "₺₺₺₺": "text-rose-600",
};

// Google Maps embed coordinates for each restaurant
const restaurantCoords: Record<string, { lat: number; lng: number; mapUrl: string }> = {
  "ciya-sofrasi": {
    lat: 40.9906,
    lng: 29.0293,
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3011.5!2d29.0293!3d40.9906!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab9e7a7777c43%3A0x4c76cf3dcc8b330b!2sÇiya%20Sofrası!5e0!3m2!1str!2str!4v1700000001",
  },
  "mikla": {
    lat: 41.0338,
    lng: 28.9784,
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3009.2!2d28.9784!3d41.0338!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab9f3b3b3b3b3%3A0x4c76cf3dcc8b330b!2sMikla%20Restaurant!5e0!3m2!1str!2str!4v1700000002",
  },
  "imam-cagdas": {
    lat: 37.0662,
    lng: 37.3833,
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3175.5!2d37.3833!3d37.0662!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1531e6a0a0a0a0a1%3A0x4c76cf3dcc8b330b!2sİmam%20Çağdaş!5e0!3m2!1str!2str!4v1700000003",
  },
  "borsam-tasievi": {
    lat: 37.3126,
    lng: 40.7351,
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3168.5!2d40.7351!3d37.3126!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1531e6a0a0a0a0a2%3A0x4c76cf3dcc8b330b!2sMardin%20Borsam%20Taşevi!5e0!3m2!1str!2str!4v1700000004",
  },
  "karadeniz-pidecisi": {
    lat: 41.0015,
    lng: 39.7178,
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3020.5!2d39.7178!3d41.0015!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x406a5b0a0a0a0a01%3A0x4c76cf3dcc8b330b!2sTrabzon%20Karadeniz%20Pidecisi!5e0!3m2!1str!2str!4v1700000005",
  },
  "assk-kahve": {
    lat: 38.2833,
    lng: 26.3769,
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3130.5!2d26.3769!3d38.2833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14bbf3a0a0a0a0a1%3A0x4c76cf3dcc8b330b!2sAlaçatı%20Aşşk%20Kahve!5e0!3m2!1str!2str!4v1700000006",
  },
};

// Map city slug to display name
const citySlugToName: Record<string, string> = {
  istanbul: "İstanbul",
  ankara: "Ankara",
  izmir: "İzmir",
  antalya: "Antalya",
  bursa: "Bursa",
  gaziantep: "Gaziantep",
  konya: "Konya",
  trabzon: "Trabzon",
  mardin: "Mardin",
};

const RestaurantsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCuisine, setSelectedCuisine] = useState("Tümü");
  const [selectedPrice, setSelectedPrice] = useState("Tümü");
  const [showOnlyNew, setShowOnlyNew] = useState(false);
  const [showOnlyWithMenu, setShowOnlyWithMenu] = useState(false);
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "map">("grid");
  const [restaurants, setRestaurants] = useState(mockRestaurants);
  const [selectedRestaurant, setSelectedRestaurant] = useState(mockRestaurants[0]);
  const [selectedRegion, setSelectedRegion] = useState("Tümü");
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [showOnlyOpen, setShowOnlyOpen] = useState(false);
  const [favToast, setFavToast] = useState<{ visible: boolean; message: string; added: boolean }>({ visible: false, message: "", added: true });
  const [favPanelOpen, setFavPanelOpen] = useState(false);

  const currentPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

  const setPage = useCallback((page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page === 1) { params.delete("page"); } else { params.set("page", String(page)); }
    setSearchParams(params, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams, setSearchParams]);

  const filtered = useMemo(() => {
    return restaurants.filter((r) => {
      const matchCuisine = selectedCuisine === "Tümü" || r.cuisine === selectedCuisine;
      const matchPrice = selectedPrice === "Tümü" || r.priceRange === selectedPrice;
      const matchSearch = !searchQuery || r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.cuisine.toLowerCase().includes(searchQuery.toLowerCase());
      const matchNew = !showOnlyNew || isNewlyAdded(r as { approvedAt?: string; ownerSubmitted?: boolean });
      const matchCity = !selectedCity || r.city === selectedCity;
      const matchRegion = selectedRegion === "Tümü" || (r as { region?: string }).region === selectedRegion;
      const matchMenu = !showOnlyWithMenu || restaurantsWithMenu.has(r.id);
      const matchFav = !showOnlyFavorites || favorites.includes(r.id);
      const matchOpen = !showOnlyOpen || isOpenNow(r.id);
      return matchCuisine && matchPrice && matchSearch && matchNew && matchCity && matchRegion && matchMenu && matchFav && matchOpen;
    });
  }, [selectedCuisine, selectedPrice, searchQuery, showOnlyNew, selectedCity, selectedRegion, showOnlyFavorites, favorites, showOnlyOpen, restaurants]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / RESTAURANTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRestaurants = filtered.slice((safePage - 1) * RESTAURANTS_PER_PAGE, safePage * RESTAURANTS_PER_PAGE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCuisine, selectedPrice, searchQuery, showOnlyNew, selectedCity, selectedRegion, showOnlyFavorites, showOnlyOpen]);

  useEffect(() => {
    const approved = JSON.parse(localStorage.getItem("approvedRestaurants") || "[]");
    const combined = approved.length > 0 ? [...mockRestaurants, ...approved] : mockRestaurants;
    setRestaurants(combined);
    setSelectedRestaurant(combined[0]);
    setFavorites(getFavorites());
  }, []);

  const handleToggleFavorite = useCallback((e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation();
    const added = toggleFavorite(id);
    setFavorites(getFavorites());
    setFavToast({ visible: true, message: added ? `"${name}" favorilere eklendi` : `"${name}" favorilerden çıkarıldı`, added });
    setTimeout(() => setFavToast((t) => ({ ...t, visible: false })), 2500);
  }, []);

  // Read city from URL param on mount
  useEffect(() => {
    const cityParam = searchParams.get("city");
    if (cityParam && citySlugToName[cityParam]) {
      setSelectedCity(citySlugToName[cityParam]);
    }
  }, [searchParams]);

  const mapRestaurant = restaurantCoords[selectedRestaurant.id];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-16 bg-stone-900 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Turkish%20restaurant%20food%20spread%20meze%20kebab%20baklava%20traditional%20cuisine%20colorful%20dishes%20table&width=1400&height=400&seq=rest-header&orientation=landscape"
          alt="Restoranlar"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">Lezzet Rehberi</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            En İyi Restoranlar
          </h1>
          <p className="text-stone-300 text-base max-w-xl mx-auto mb-6">
            Türkiye'nin dört bir yanından, editörlerimizin bizzat deneyimlediği en iyi lezzet durakları.
          </p>
          <div className="flex items-center gap-3 justify-center flex-wrap">
            <button
              onClick={() => navigate("/restaurant-apply")}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-circle-line"></i> Restoranınızı Ekleyin
            </button>
            <button
              onClick={() => setFavPanelOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-full text-sm font-medium hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-heart-fill text-rose-400"></i>
              Favorilerim
              {favorites.length > 0 && (
                <span className="bg-rose-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">{favorites.length}</span>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Region Map Selector */}
      <section className="bg-stone-50 border-b border-stone-100 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-5">
            <div>
              <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Bölgeye Göre Keşfet</span>
              <h2 className="text-lg font-bold text-stone-900 mt-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                Türkiye'nin Lezzet Bölgeleri
              </h2>
            </div>
            {selectedRegion !== "Tümü" && (
              <button
                onClick={() => setSelectedRegion("Tümü")}
                className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line"></i> Filtreyi Kaldır
              </button>
            )}
          </div>

          {/* Region Cards Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {/* All button */}
            <button
              onClick={() => setSelectedRegion("Tümü")}
              className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer
                ${selectedRegion === "Tümü"
                  ? "border-stone-800 bg-stone-800 text-white"
                  : "border-stone-200 bg-white text-stone-600 hover:border-stone-400"
                }`}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-map-2-line text-xl"></i>
              </div>
              <span className="text-xs font-semibold text-center leading-tight">Tümü</span>
              <span className={`text-xs ${selectedRegion === "Tümü" ? "text-stone-300" : "text-stone-400"}`}>
                {restaurants.length}
              </span>
            </button>

            {Object.entries(regionInfoMap).map(([key, info]) => {
              const count = restaurants.filter((r) => (r as { region?: string }).region === key).length;
              const isActive = selectedRegion === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedRegion(isActive ? "Tümü" : key)}
                  className={`relative flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer overflow-hidden
                    ${isActive
                      ? `border-transparent ${info.activeBg} text-white`
                      : "border-stone-200 bg-white hover:border-stone-300"
                    }`}
                >
                  {/* Background image subtle */}
                  {!isActive && (
                    <div className="absolute inset-0 opacity-5">
                      <img src={info.image} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className={`relative w-8 h-8 flex items-center justify-center ${isActive ? "text-white" : info.color}`}>
                    <i className={`${info.icon} text-xl`}></i>
                  </div>
                  <span className={`relative text-xs font-semibold text-center leading-tight ${isActive ? "text-white" : "text-stone-700"}`}>
                    {info.name}
                  </span>
                  <span className={`relative text-xs ${isActive ? "text-white/80" : "text-stone-400"}`}>
                    {count} restoran
                  </span>
                </button>
              );
            })}
          </div>

          {/* Selected region detail */}
          {selectedRegion !== "Tümü" && regionInfoMap[selectedRegion] && (
            <div className="mt-4 flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-200">
              <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
                <img src={regionInfoMap[selectedRegion].image} alt={selectedRegion} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <i className={`${regionInfoMap[selectedRegion].icon} ${regionInfoMap[selectedRegion].color} text-sm`}></i>
                  <span className="font-semibold text-stone-900 text-sm">{selectedRegion} Bölgesi</span>
                </div>
                <p className="text-stone-500 text-xs">{regionInfoMap[selectedRegion].description}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {regionInfoMap[selectedRegion].cities.map((city) => (
                    <span key={city} className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">{city}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 z-30 bg-white border-b border-stone-100 py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex items-center bg-stone-50 border border-stone-200 rounded-full px-4 py-2 gap-2 flex-1 max-w-xs">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-stone-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Restoran veya şehir ara..."
                className="bg-transparent text-sm text-stone-700 outline-none flex-1"
              />
            </div>

            {/* Price Range Filter — prominent */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-xs text-stone-400 font-medium whitespace-nowrap">Bütçe:</span>
              {priceFilters.map((p) => {
                const priceLabel: Record<string, string> = {
                  "Tümü": "Tümü",
                  "₺": "₺ Uygun",
                  "₺₺": "₺₺ Orta",
                  "₺₺₺": "₺₺₺ Üst",
                  "₺₺₺₺": "₺₺₺₺ Lüks",
                };
                const priceActive: Record<string, string> = {
                  "Tümü": "bg-stone-800 text-white",
                  "₺": "bg-emerald-500 text-white",
                  "₺₺": "bg-amber-500 text-white",
                  "₺₺₺": "bg-orange-500 text-white",
                  "₺₺₺₺": "bg-rose-500 text-white",
                };
                return (
                  <button
                    key={p}
                    onClick={() => setSelectedPrice(p)}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap border ${
                      selectedPrice === p
                        ? `${priceActive[p]} border-transparent`
                        : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    {priceLabel[p]}
                  </button>
                );
              })}
            </div>

            {/* View Toggle */}
            <div className="flex items-center bg-stone-100 rounded-full p-1 gap-1 flex-shrink-0">
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
                  ${viewMode === "grid" ? "bg-white text-stone-800 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
              >
                <i className="ri-grid-line"></i> Kart
              </button>
              <button
                onClick={() => setViewMode("map")}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
                  ${viewMode === "map" ? "bg-white text-stone-800 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
              >
                <i className="ri-map-2-line"></i> Harita
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap mt-3">
            {cuisines.map((c) => (
              <button
                key={c}
                onClick={() => setSelectedCuisine(c)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors cursor-pointer whitespace-nowrap
                  ${selectedCuisine === c ? "border-amber-500 text-amber-600 bg-amber-50" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
              >
                {c}
              </button>
            ))}
            <div className="w-px h-4 bg-stone-200 mx-1"></div>
            <button
              onClick={() => setShowOnlyNew(!showOnlyNew)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border font-medium transition-all cursor-pointer whitespace-nowrap
                ${showOnlyNew
                  ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                  : "border-stone-200 text-stone-500 hover:border-emerald-300 hover:text-emerald-600"
                }`}
            >
              <i className="ri-sparkling-line text-xs"></i>
              Yeni Eklendi
              {showOnlyNew && (
                <span className="ml-0.5 w-3.5 h-3.5 flex items-center justify-center bg-emerald-500 text-white rounded-full text-xs leading-none">
                  <i className="ri-check-line" style={{ fontSize: 9 }}></i>
                </span>
              )}
            </button>
            <button
              onClick={() => setShowOnlyWithMenu(!showOnlyWithMenu)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border font-medium transition-all cursor-pointer whitespace-nowrap
                ${showOnlyWithMenu
                  ? "border-amber-500 text-amber-700 bg-amber-50"
                  : "border-stone-200 text-stone-500 hover:border-amber-300 hover:text-amber-600"
                }`}
            >
              <i className="ri-restaurant-2-line text-xs"></i>
              Menü Var
              {showOnlyWithMenu && (
                <span className="ml-0.5 w-3.5 h-3.5 flex items-center justify-center bg-amber-500 text-white rounded-full text-xs leading-none">
                  <i className="ri-check-line" style={{ fontSize: 9 }}></i>
                </span>
              )}
            </button>
            <button
              onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border font-medium transition-all cursor-pointer whitespace-nowrap
                ${showOnlyFavorites
                  ? "border-rose-400 text-rose-600 bg-rose-50"
                  : "border-stone-200 text-stone-500 hover:border-rose-300 hover:text-rose-500"
                }`}
            >
              <i className="ri-heart-line text-xs"></i>
              Favorilerim
              {favorites.length > 0 && (
                <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${showOnlyFavorites ? "bg-rose-500 text-white" : "bg-stone-100 text-stone-500"}`}>
                  {favorites.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowOnlyOpen(!showOnlyOpen)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border font-medium transition-all cursor-pointer whitespace-nowrap
                ${showOnlyOpen
                  ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                  : "border-stone-200 text-stone-500 hover:border-emerald-300 hover:text-emerald-600"
                }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${showOnlyOpen ? "bg-emerald-500" : "bg-stone-300"}`}></span>
              Şu An Açık
              {showOnlyOpen && (
                <span className="ml-0.5 w-3.5 h-3.5 flex items-center justify-center bg-emerald-500 text-white rounded-full text-xs leading-none">
                  <i className="ri-check-line" style={{ fontSize: 9 }}></i>
                </span>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <p className="text-sm text-stone-400">
              {filtered.length} restoran bulundu
              {totalPages > 1 && viewMode === "grid" && (
                <span className="ml-2 text-stone-300">— Sayfa {safePage} / {totalPages}</span>
              )}
            </p>
            {selectedPrice !== "Tümü" && (
              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-200 font-medium">
                <i className="ri-money-cny-circle-line"></i> {selectedPrice} fiyat aralığı
                <button
                  onClick={() => setSelectedPrice("Tümü")}
                  className="ml-1 w-3.5 h-3.5 flex items-center justify-center hover:bg-amber-200 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" style={{ fontSize: 10 }}></i>
                </button>
              </span>
            )}
            {selectedRegion !== "Tümü" && (
              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 bg-stone-100 text-stone-700 rounded-full border border-stone-200 font-medium">
                <i className="ri-map-2-line"></i> {selectedRegion}
                <button
                  onClick={() => setSelectedRegion("Tümü")}
                  className="ml-1 w-3.5 h-3.5 flex items-center justify-center hover:bg-stone-300 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" style={{ fontSize: 10 }}></i>
                </button>
              </span>
            )}
            {selectedCity && (
              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-200 font-medium">
                <i className="ri-map-pin-line"></i> {selectedCity}
                <button
                  onClick={() => { setSelectedCity(""); setSearchParams({}); }}
                  className="ml-1 w-3.5 h-3.5 flex items-center justify-center hover:bg-amber-200 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" style={{ fontSize: 10 }}></i>
                </button>
              </span>
            )}
            {showOnlyNew && (
              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200 font-medium">
                <i className="ri-sparkling-line"></i> Son 30 günde eklenenler
                <button
                  onClick={() => setShowOnlyNew(false)}
                  className="ml-1 w-3.5 h-3.5 flex items-center justify-center hover:bg-emerald-200 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" style={{ fontSize: 10 }}></i>
                </button>
              </span>
            )}
            {showOnlyWithMenu && (
              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-200 font-medium">
                <i className="ri-restaurant-2-line"></i> Menüsü olan restoranlar
                <button
                  onClick={() => setShowOnlyWithMenu(false)}
                  className="ml-1 w-3.5 h-3.5 flex items-center justify-center hover:bg-amber-200 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" style={{ fontSize: 10 }}></i>
                </button>
              </span>
            )}
            {showOnlyOpen && (
              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"></span> Şu an açık restoranlar
                <button
                  onClick={() => setShowOnlyOpen(false)}
                  className="ml-1 w-3.5 h-3.5 flex items-center justify-center hover:bg-emerald-200 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" style={{ fontSize: 10 }}></i>
                </button>
              </span>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                {showOnlyNew
                  ? <i className="ri-sparkling-line text-emerald-300 text-4xl"></i>
                  : <i className="ri-restaurant-line text-stone-300 text-4xl"></i>
                }
              </div>
              {showOnlyNew ? (
                <>
                  <p className="text-stone-600 font-medium mb-1">Henüz yeni eklenen restoran yok</p>
                  <p className="text-stone-400 text-sm">Son 30 günde eklenen restoran bulunmuyor.</p>
                  <button
                    onClick={() => setShowOnlyNew(false)}
                    className="mt-4 px-4 py-2 text-sm text-emerald-600 border border-emerald-200 rounded-full hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Tüm Restoranları Göster
                  </button>
                </>
              ) : (
                <p className="text-stone-400">Arama kriterlerinize uygun restoran bulunamadı.</p>
              )}
            </div>
          ) : viewMode === "grid" ? (
            <>
            <div className="divide-y divide-stone-100">
              {paginatedRestaurants.map((rest) => (
                <div key={rest.id} className="group flex items-center gap-5 py-4 hover:bg-stone-50 px-2 transition-colors">
                  <Link to={`/restaurant/${rest.id}`} className="flex items-center gap-5 flex-1 min-w-0">
                    <div className="relative w-20 h-20 flex-shrink-0 overflow-hidden rounded-lg">
                      <img
                        src={rest.image}
                        alt={rest.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs text-stone-400">{rest.cuisine}</span>
                        <span className="text-stone-200">·</span>
                        <span className="text-xs text-stone-400">{rest.city}, {rest.district}</span>
                        {(() => {
                          const status = getOpenStatus(rest.id);
                          return (
                            <span className={`flex items-center gap-1 text-xs font-medium ${status.open ? "text-emerald-600" : "text-stone-400"}`}>
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${status.open ? "bg-emerald-500" : "bg-stone-300"}`}></span>
                              {status.label}
                            </span>
                          );
                        })()}
                      </div>
                      <h3 className="font-bold text-stone-900 text-base group-hover:text-amber-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {rest.name}
                      </h3>
                      <p className="text-stone-500 text-xs leading-relaxed line-clamp-1 mt-0.5">{rest.shortDesc}</p>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                          <span className="text-xs font-semibold text-stone-700">{rest.rating}</span>
                        </div>
                        <span className={`text-sm font-bold ${priceColors[rest.priceRange] || "text-stone-600"}`}>{rest.priceRange}</span>
                        {rest.mustTry.slice(0, 2).map((item) => (
                          <span key={item} className="text-xs text-amber-600">#{item}</span>
                        ))}
                      </div>
                    </div>
                  </Link>
                  <button
                    onClick={(e) => handleToggleFavorite(e, rest.id, rest.name)}
                    className={`w-8 h-8 flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${favorites.includes(rest.id) ? "text-rose-500" : "text-stone-300 hover:text-rose-400"}`}
                  >
                    <i className={favorites.includes(rest.id) ? "ri-heart-fill" : "ri-heart-line"}></i>
                  </button>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-stone-300 group-hover:text-amber-500 transition-colors">
                    <i className="ri-arrow-right-s-line"></i>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex items-center justify-center gap-2 flex-wrap">
                <button
                  onClick={() => setPage(safePage - 1)}
                  disabled={safePage === 1}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    safePage === 1 ? "bg-stone-100 text-stone-300 cursor-not-allowed" : "bg-stone-100 text-stone-600 hover:bg-stone-200 cursor-pointer"
                  }`}
                >
                  <i className="ri-arrow-left-s-line"></i> Önceki
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                  const isActive = p === safePage;
                  const isNear = Math.abs(p - safePage) <= 2 || p === 1 || p === totalPages;
                  if (!isNear) {
                    if (p === safePage - 3 || p === safePage + 3) return <span key={p} className="text-stone-300 text-sm px-1">…</span>;
                    return null;
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                        isActive ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-amber-100 hover:text-amber-700"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(safePage + 1)}
                  disabled={safePage === totalPages}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    safePage === totalPages ? "bg-stone-100 text-stone-300 cursor-not-allowed" : "bg-stone-100 text-stone-600 hover:bg-stone-200 cursor-pointer"
                  }`}
                >
                  Sonraki <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
            {totalPages > 1 && (
              <p className="mt-4 text-center text-xs text-stone-300">
                Toplam {filtered.length} restoran — {totalPages} sayfa
              </p>
            )}
            </>
          ) : (
            /* MAP VIEW — Advanced with all pins */
            <div className="flex gap-5 h-[680px]">
              {/* Left: Restaurant List */}
              <div className="w-72 flex-shrink-0 flex flex-col gap-3 min-h-0">
                {/* Stats bar */}
                <div className="flex items-center gap-3 px-3 py-2 bg-stone-50 rounded-xl border border-stone-100">
                  <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></div>
                  <span className="text-xs text-stone-500 font-medium">{filtered.length} restoran haritada</span>
                  <div className="ml-auto flex items-center gap-1 text-xs text-stone-400">
                    <i className="ri-cursor-line text-xs"></i>
                    <span>Seç</span>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                  {filtered.map((rest, idx) => (
                    <button
                      key={rest.id}
                      onClick={() => setSelectedRestaurant(rest)}
                      className={`w-full text-left flex gap-3 p-3 rounded-xl border transition-all cursor-pointer
                        ${selectedRestaurant.id === rest.id
                          ? "border-amber-400 bg-amber-50"
                          : "border-stone-100 bg-white hover:border-amber-200 hover:bg-stone-50"
                        }`}
                    >
                      {/* Pin number */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${
                        selectedRestaurant.id === rest.id ? "bg-amber-500 text-white" : "bg-stone-200 text-stone-600"
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <h4 className="font-semibold text-stone-900 text-xs truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                            {rest.name}
                          </h4>
                          <span className={`text-xs font-bold flex-shrink-0 ${priceColors[rest.priceRange] || "text-stone-600"}`}>
                            {rest.priceRange}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <i className="ri-map-pin-line text-amber-500 text-xs"></i>
                          <span className="text-xs text-stone-400 truncate">{rest.city}</span>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                          <span className="text-xs font-semibold text-stone-700">{rest.rating}</span>
                          <span className="text-xs text-stone-400">· {rest.cuisine}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: Map + Info */}
              <div className="flex-1 flex flex-col gap-3 min-w-0">
                {/* Selected restaurant info card */}
                <div
                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-amber-200 cursor-pointer hover:border-amber-400 transition-colors flex-shrink-0"
                  onClick={() => navigate(`/restaurant/${selectedRestaurant.id}`)}
                >
                  <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                    <img src={selectedRestaurant.image} alt={selectedRestaurant.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-stone-900 text-sm truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {selectedRestaurant.name}
                    </h3>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <div className="flex items-center gap-1">
                        <i className="ri-map-pin-line text-amber-500 text-xs"></i>
                        <span className="text-xs text-stone-500">{selectedRestaurant.city}, {selectedRestaurant.district}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        <span className="text-xs font-semibold text-stone-700">{selectedRestaurant.rating}</span>
                      </div>
                      <span className={`text-xs font-bold ${priceColors[selectedRestaurant.priceRange] || "text-stone-600"}`}>
                        {selectedRestaurant.priceRange}
                      </span>
                      {(() => {
                        const status = getOpenStatus(selectedRestaurant.id);
                        return (
                          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                            status.open ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${status.open ? "bg-emerald-500" : "bg-stone-400"}`}></span>
                            {status.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-amber-600 text-xs font-medium flex-shrink-0 whitespace-nowrap">
                    <span>Detay</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>

                {/* Google Maps Embed — shows all restaurants as search results */}
                <div className="flex-1 rounded-2xl overflow-hidden border border-stone-200 relative">
                  {mapRestaurant ? (
                    <iframe
                      key={selectedRestaurant.id}
                      title={`${selectedRestaurant.name} Harita`}
                      src={mapRestaurant.mapUrl}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  ) : (
                    /* Fallback: show a search map for the city */
                    <iframe
                      key={`city-${selectedRestaurant.city}`}
                      title={`${selectedRestaurant.city} Restoranlar Harita`}
                      src={`https://maps.google.com/maps?q=restaurants+in+${encodeURIComponent(selectedRestaurant.city + " Turkey")}&z=13&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  )}

                  {/* Overlay pin legend */}
                  <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 border border-stone-200 max-w-[200px]">
                    <p className="text-xs font-semibold text-stone-700 mb-2">Haritadaki Restoranlar</p>
                    <div className="space-y-1 max-h-28 overflow-y-auto">
                      {filtered.slice(0, 8).map((r, idx) => (
                        <button
                          key={r.id}
                          onClick={() => setSelectedRestaurant(r)}
                          className={`w-full flex items-center gap-2 text-left cursor-pointer rounded-lg px-1.5 py-1 transition-colors ${
                            selectedRestaurant.id === r.id ? "bg-amber-50" : "hover:bg-stone-50"
                          }`}
                        >
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                            selectedRestaurant.id === r.id ? "bg-amber-500 text-white" : "bg-stone-200 text-stone-600"
                          }`}>
                            {idx + 1}
                          </span>
                          <span className="text-xs text-stone-700 truncate">{r.name}</span>
                        </button>
                      ))}
                      {filtered.length > 8 && (
                        <p className="text-xs text-stone-400 text-center pt-1">+{filtered.length - 8} daha</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Favorites Panel */}
      {favPanelOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/40" onClick={() => setFavPanelOpen(false)}></div>
          <div className="w-80 bg-white h-full flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-stone-100">
              <div className="flex items-center gap-2">
                <i className="ri-heart-fill text-rose-500"></i>
                <h3 className="font-bold text-stone-900 text-base">Favorilerim</h3>
                <span className="bg-rose-100 text-rose-600 text-xs px-2 py-0.5 rounded-full font-bold">{favorites.length}</span>
              </div>
              <button onClick={() => setFavPanelOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-stone-100 rounded-full cursor-pointer">
                <i className="ri-close-line text-stone-500"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {favorites.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-heart-line text-stone-300 text-3xl"></i>
                  </div>
                  <p className="text-stone-400 text-sm">Henüz favori eklemediniz.</p>
                  <p className="text-stone-300 text-xs mt-1">Restoran kartlarındaki kalp ikonuna tıklayın.</p>
                </div>
              ) : (
                restaurants.filter((r) => favorites.includes(r.id)).map((r) => (
                  <div
                    key={r.id}
                    onClick={() => { setFavPanelOpen(false); navigate(`/restaurant/${r.id}`); }}
                    className="flex gap-3 p-3 rounded-xl border border-stone-100 hover:border-rose-200 hover:bg-rose-50 cursor-pointer transition-all"
                  >
                    <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={r.image} alt={r.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-stone-900 text-sm truncate">{r.name}</h4>
                      <p className="text-stone-400 text-xs mt-0.5">{r.city} · {r.cuisine}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        <span className="text-xs font-semibold text-stone-700">{r.rating}</span>
                        <span className={`text-xs font-bold ${priceColors[r.priceRange] || ""}`}>{r.priceRange}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleFavorite(e, r.id, r.name); }}
                      className="w-7 h-7 flex items-center justify-center text-rose-400 hover:text-rose-600 cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-heart-fill text-sm"></i>
                    </button>
                  </div>
                ))
              )}
            </div>
            {favorites.length > 0 && (
              <div className="p-4 border-t border-stone-100">
                <button
                  onClick={() => { localStorage.setItem(FAVORITES_KEY, "[]"); setFavorites([]); }}
                  className="w-full text-xs text-stone-400 hover:text-rose-500 transition-colors cursor-pointer py-2"
                >
                  Tüm favorileri temizle
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fav Toast */}
      <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-all duration-300 ${favToast.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"} ${favToast.added ? "bg-rose-500 text-white" : "bg-stone-700 text-white"}`}>
        <i className={favToast.added ? "ri-heart-fill" : "ri-heart-line"}></i>
        {favToast.message}
      </div>

      <Footer />
    </div>
  );
};

export default RestaurantsPage;
